// Auth resilience — survives wrong client clocks and transient identity drops.
//
// The Supabase SDK schedules token renewal off the LOCAL clock (GoTrueClient
// compares session.expires_at against Date.now()). A skewed machine clock makes
// it mistime renewals, so requests intermittently go out without a user JWT and
// the server correctly treats them as anonymous: SELECTs return empty rows,
// writes fail RLS ("permission denied"), and the SDK eventually fires
// SIGNED_OUT — all while the user believes they're logged in.
//
// Recovery is clock-immune because refresh tokens are validated by the
// SERVER's clock. So: when an operation fails in a way that looks like an
// identity drop, force one server-validated refresh and retry the operation
// once. A genuinely revoked session still fails and surfaces normally.

import { supabase } from '@/lib/safeSupabase';
import type { Session, User } from '@supabase/supabase-js';

const AUTH_FAILURE_PATTERNS: RegExp[] = [
  /row-level security/i,
  /\b42501\b/,
  /permission denied/i,
  /jwt expired/i,
  /invalid jwt/i,
  /refresh_token_not_found/i,
];

/** True when an error means "the server didn't recognize this request's user". */
export function isAuthFailure(err: unknown): boolean {
  if (!err) return false;
  const e = err as { message?: unknown; status?: unknown; code?: unknown };
  if (e.status === 401 || e.status === 403) return true;
  // PGRST301 = PostgREST "JWT expired"
  if (e.code === '42501' || e.code === 'PGRST301') return true;
  const msg =
    err instanceof Error ? err.message :
    typeof err === 'string' ? err :
    typeof e.message === 'string' ? e.message : '';
  return AUTH_FAILURE_PATTERNS.some((p) => p.test(msg));
}

const RATE_LIMIT_PATTERNS: RegExp[] = [
  /over_request_rate_limit/i,
  /rate limit/i,
  /too many requests/i,
];

/** True when the server is telling us to slow down (HTTP 429 / GoTrue rate limit). */
export function isRateLimited(err: unknown): boolean {
  if (!err) return false;
  const e = err as { message?: unknown; status?: unknown; code?: unknown };
  if (e.status === 429) return true;
  if (e.code === 'over_request_rate_limit') return true;
  const msg =
    err instanceof Error ? err.message :
    typeof err === 'string' ? err :
    typeof e.message === 'string' ? e.message : '';
  return RATE_LIMIT_PATTERNS.some((p) => p.test(msg));
}

function messageOf(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  const e = err as { message?: unknown };
  return typeof e?.message === 'string' ? e.message : '';
}

/** Thrown when the session is unrecoverable — surfaces as "session expired", never a permissions error. */
export class SessionExpiredError extends Error {
  constructor() {
    super('session_expired');
    this.name = 'SessionExpiredError';
  }
}

// The app registers a handler that cleans up locally and routes to login.
let sessionExpiredHandler: (() => void) | null = null;
export function setSessionExpiredHandler(fn: (() => void) | null): void {
  sessionExpiredHandler = fn;
}

// A refresh failing this way means the token family is dead — recovery is futile.
const UNRECOVERABLE_PATTERNS: RegExp[] = [
  /refresh_token_not_found/i,
  /invalid refresh token/i,
  /already used/i, // refresh_token_already_used — lost the rotation race
];

// Refreshing harder when the server says "too many requests" is what turns a
// blip into a stampede. On 429, stop and back off exponentially (30s, 60s, …).
const RATE_LIMIT_BASE_MS = 30_000;
const RATE_LIMIT_MAX_MS = 5 * 60_000;
let rateLimitStreak = 0;
let rateLimitedUntil = 0;

function armRateLimitBackoff(): void {
  const delay = Math.min(RATE_LIMIT_BASE_MS * 2 ** rateLimitStreak, RATE_LIMIT_MAX_MS);
  rateLimitStreak += 1;
  rateLimitedUntil = Date.now() + delay;
}

// Single-flight: concurrent failures share one refresh round-trip instead of
// stampeding the token endpoint (rapid rotation can trip reuse detection).
let recovery: Promise<boolean> | null = null;
// Set true when the last refresh failed in a way that cannot be recovered.
let lastRefreshUnrecoverable = false;

/** Force a server-validated session refresh. Resolves true if a live session came back. */
export function recoverSession(): Promise<boolean> {
  // Backoff: if we were just rate-limited, fail fast instead of adding another
  // refresh to a storm the server is already rejecting.
  if (Date.now() < rateLimitedUntil) return Promise.resolve(false);
  if (!recovery) {
    lastRefreshUnrecoverable = false;
    recovery = supabase.auth
      .refreshSession()
      .then(({ data, error }) => {
        if (error) {
          if (isRateLimited(error)) armRateLimitBackoff();
          else if (UNRECOVERABLE_PATTERNS.some((p) => p.test(messageOf(error)))) lastRefreshUnrecoverable = true;
          return false;
        }
        rateLimitStreak = 0;
        return !!data.session;
      })
      .catch((e) => {
        if (isRateLimited(e)) armRateLimitBackoff();
        return false;
      })
      .finally(() => {
        recovery = null;
      });
  }
  return recovery;
}

/** Run an operation; on an identity-drop failure, refresh the session and retry once. */
export async function withAuthRetry<T>(op: () => Promise<T>): Promise<T> {
  try {
    return await op();
  } catch (err) {
    // Never refresh-retry into a rate limit — that pours fuel on the fire.
    if (isRateLimited(err) || !isAuthFailure(err)) throw err;
    if (await recoverSession()) return op();
    // Recovery failed. If the refresh token is genuinely gone, the session is
    // dead — sign out cleanly with a session-expired message instead of letting
    // a misleading "permission denied" surface. Rate-limited/transient failures
    // fall through to the original error so the user can simply try again.
    if (lastRefreshUnrecoverable) {
      sessionExpiredHandler?.();
      throw new SessionExpiredError();
    }
    throw err;
  }
}

/** Test-only: reset module-level state between tests. */
export function __resetAuthResilienceState(): void {
  recovery = null;
  rateLimitedUntil = 0;
  rateLimitStreak = 0;
  lastRefreshUnrecoverable = false;
  sessionExpiredHandler = null;
  clockSkewMs = 0;
  sawSessionToken = false;
}

/** getUser, but attempts one session recovery before concluding "not signed in". */
export async function ensureUser(): Promise<User | null> {
  const { data } = await supabase.auth.getUser();
  if (data.user) return data.user;
  if (!(await recoverSession())) return null;
  const { data: retry } = await supabase.auth.getUser();
  return retry.user ?? null;
}

// ---- Server-clock estimation ----
// The access token's `iat` claim is stamped by the server at issue time, so at
// the moment a fresh token arrives, server time ≈ iat. Tracking the offset lets
// time comparisons (e.g. trial expiry) use the server's clock, not the laptop's.

let clockSkewMs = 0;

/** Current time as the server sees it (local clock + measured skew). */
export function serverNow(): number {
  return Date.now() + clockSkewMs;
}

/** Measured client-vs-server clock offset in ms (server − client). 0 until a fresh token is seen. */
export function getClockSkewMs(): number {
  return clockSkewMs;
}

/** Offset beyond which the clock is wrong enough to actually break auth.
 * The refresh-loop boot needs the clock off by close to the access-token lifetime
 * (~1h), so a few minutes of normal drift causes no problems and must NOT nag the
 * user. 30 minutes only fires for a genuinely-misconfigured clock (DST/timezone
 * mistakes, manually-set wrong time) that's in the danger zone. */
export const CLOCK_SKEW_WARN_MS = 30 * 60_000;

/** Human description of a clock that's off, or null if within tolerance. Pure. */
export function describeClockSkew(skewMs: number, thresholdMs: number = CLOCK_SKEW_WARN_MS): string | null {
  const abs = Math.abs(skewMs);
  if (abs < thresholdMs) return null;
  // Bucket by actual magnitude so e.g. 30 min reports as minutes, not a rounded-up "1 hour".
  if (abs < 3_600_000) {
    const mins = Math.round(abs / 60_000);
    return `about ${mins} minute${mins === 1 ? '' : 's'}`;
  }
  if (abs < 2 * 86_400_000) {
    const hours = Math.round(abs / 3_600_000);
    return `about ${hours} hour${hours === 1 ? '' : 's'}`;
  }
  const days = Math.round(abs / 86_400_000);
  return `about ${days} days`;
}

// Whether this tab has already held a session token. SIGNED_IN is fresh only
// when the tab had none: the auth library also fires SIGNED_IN when a tab comes
// back into view, carrying the token it already had (auth-js
// `_recoverAndRefresh`), and that token's iat is as old as the token. Drew,
// 2026-11-04: his own Errors cards all printed a 34-minute clock skew from a
// tab refocused 34 minutes after its token was issued; his clock is set
// automatically and was right.
let sawSessionToken = false;

/** Feed every auth event here. The skew estimate moves only on a token minted
 *  just now: TOKEN_REFRESHED (this tab, or another tab's, rebroadcast at once),
 *  or SIGNED_IN into a tab that had no session. A restored session (INITIAL_SESSION,
 *  or SIGNED_IN on refocus) only marks the tab as holding one. */
export function noteAuthEvent(event: string, session: Session | null): void {
  if (event === 'SIGNED_OUT') { sawSessionToken = false; return; }
  if (!session?.access_token) return;
  const fresh = event === 'TOKEN_REFRESHED' || (event === 'SIGNED_IN' && !sawSessionToken);
  sawSessionToken = true;
  if (fresh) updateClockSkew(session);
}

/** Update the skew estimate from a session whose token was issued just now.
 *  Callers go through noteAuthEvent, which knows which events carry one. */
export function updateClockSkew(session: Session | null): void {
  const token = session?.access_token;
  if (!token) return;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof payload.iat === 'number') {
      clockSkewMs = payload.iat * 1000 - Date.now();
    }
  } catch {
    // Malformed token — keep the previous estimate.
  }
}
